import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tag, Printer, Clock, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { QRCodeSVG } from "qrcode.react";
import { openPrintWindow } from "@/utils/printHelper";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company logo URL (the cube logo)
const LOGO_URL = "/challenge-pack-logo.png";

const LabelPrinting = ({ variant = "button" }) => {
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [bitReelsForLabel, setBitReelsForLabel] = useState([]);
  const [loadingBitReels, setLoadingBitReels] = useState(false);
  const [pendingLabelsCount, setPendingLabelsCount] = useState(0);
  const [selectedLabelReel, setSelectedLabelReel] = useState(null);
  const [showSizeSelector, setShowSizeSelector] = useState(false);
  const [reelToPrint, setReelToPrint] = useState(null);
  
  // Label size state - simplified to 3x5 default with edit option and manual entry
  const [useCustomSize, setUseCustomSize] = useState(false);
  const [editingStandardSize, setEditingStandardSize] = useState(false);
  const [standardLabelWidth, setStandardLabelWidth] = useState("3");
  const [standardLabelHeight, setStandardLabelHeight] = useState("4.5");
  const [customLabelWidth, setCustomLabelWidth] = useState("");
  const [customLabelHeight, setCustomLabelHeight] = useState("");
  
  const labelPrintRef = useRef(null);

  // Calculate days since creation for overdue warning
  const calculateDaysPending = (createdAt) => {
    if (!createdAt) return 0;
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const oldestUnprintedDays = bitReelsForLabel.length > 0 
    ? Math.max(...bitReelsForLabel.map(r => r.days_pending || 0))
    : 0;

  // Fetch bit reels that are ready for label printing
  const fetchBitReelsForLabel = async (updateCountOnly = false) => {
    try {
      if (!updateCountOnly) setLoadingBitReels(true);
      const response = await axios.get(`${API}/inventory`);
      
      // Filter for bit reels that are available, have a parent, and label not yet printed
      const bitReels = response.data.filter(item => 
        item.is_bit_reel === true &&
        item.parent_reel_id &&
        (item.status === "available" || item.status === "return" || !item.status) &&
        item.label_printed !== true
      );

      // Calculate days pending for each
      const enrichedBitReels = bitReels.map(reel => ({
        ...reel,
        days_pending: calculateDaysPending(reel.in_date || reel.created_at)
      }));

      // Sort by days pending (oldest first)
      enrichedBitReels.sort((a, b) => (b.days_pending || 0) - (a.days_pending || 0));

      setPendingLabelsCount(enrichedBitReels.length);

      if (!updateCountOnly) {
        // Enrich with parent reel info
        const enrichedWithParent = await Promise.all(enrichedBitReels.map(async (reel) => {
          try {
            const parentRes = await axios.get(`${API}/inventory`);
            const parent = parentRes.data.find(p => p.reel_id === reel.parent_reel_id);
            return {
              ...reel,
              parent_weight: parent?.reel_weight || reel.original_weight,
              parent_rm_name: parent?.rm_name,
              parent_reel_size: parent?.reel_size
            };
          } catch {
            return reel;
          }
        }));
        setBitReelsForLabel(enrichedWithParent);
      }
    } catch (error) {
      console.error("Error fetching bit reels for label:", error);
    } finally {
      if (!updateCountOnly) setLoadingBitReels(false);
    }
  };

  // Fetch pending labels count on component mount
  useEffect(() => {
    fetchBitReelsForLabel(true);
    // Refresh count every 30 seconds
    const interval = setInterval(() => fetchBitReelsForLabel(true), 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle opening label dialog
  const handleOpenLabelDialog = () => {
    fetchBitReelsForLabel();
    setShowLabelDialog(true);
  };

  // Handle marking label as printed
  const handleMarkLabelPrinted = async (reelId) => {
    try {
      await axios.put(`${API}/inventory/${reelId}/label-printed`);
      toast.success("Label marked as printed");
      setShowSizeSelector(false);
      setReelToPrint(null);
      fetchBitReelsForLabel();
    } catch (error) {
      console.error("Error marking label as printed:", error);
      toast.error("Failed to mark label as printed");
    }
  };

  // Handle print for a specific label
  const handlePrintLabel = useReactToPrint({
    contentRef: labelPrintRef,
    documentTitle: selectedLabelReel ? `Label-${selectedLabelReel.reel_id}` : "Label",
    onAfterPrint: () => {
      if (selectedLabelReel) {
        handleMarkLabelPrinted(selectedLabelReel.reel_id);
      }
    }
  });

  // Handle print button click - show size selector first
  const handlePrintClick = (reel) => {
    setReelToPrint(reel);
    setShowSizeSelector(true);
  };

  // State for Google Drive upload
  const [uploadingLabel, setUploadingLabel] = useState(false);

  // Confirm size and print - now uploads to Google Drive first
  const confirmAndPrint = async () => {
    if (!reelToPrint) return;
    
    setUploadingLabel(true);
    
    try {
      // Upload to Google Drive
      const response = await axios.post(`${API}/labels/generate-and-upload`, {
        stock_id: reelToPrint.reel_id,
        rm_name: reelToPrint.rm_name || reelToPrint.parent_rm_name || '',
        reel_size: reelToPrint.reel_size?.toString() || '',
        mill_reel_no: reelToPrint.mill_reel_no || '',
        weight: reelToPrint.reel_weight?.toString() || '',
        product: reelToPrint.product || '',
        mill_name: reelToPrint.mill_name || '',
        in_date: reelToPrint.in_date || '',
        label_width: getLabelDimensions().width,
        label_height: getLabelDimensions().height,
        is_bit_reel: true,
        parent_stock_id: reelToPrint.parent_reel_id || ''
      });
      
      if (response.data.success) {
        // Check if already uploaded
        if (response.data.already_uploaded) {
          toast.info(`Label already uploaded. Using existing link.`);
        } else {
          toast.success(`Label uploaded to Google Drive: ${response.data.filename}`);
        }
        
        // Open print window with the Drive-linked label
        const { width, height } = getLabelDimensions();
        const driveLink = response.data.direct_link || response.data.shareable_link;
        
        // Generate QR code as data URL using Google Chart API (reliable, no external script needed)
        const qrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(driveLink)}&ecc=H`;
        
        // Generate HTML for printing with clean white design
        const htmlContent = `<!DOCTYPE html><html><head>
          <title> </title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: ${width}in ${height}in; margin: 0; }
            html, body { font-family: Arial, sans-serif; margin: 0; padding: 0; background: white; }

            .label { width: ${width}in; height: ${height}in; padding: 3px; page-break-after: always; page-break-inside: avoid; box-sizing: border-box; background: white; overflow: hidden; }
            /* Inner content clamped to 3.3in so browser-added header/footer never pushes content onto next sticker */
            .label-inner { border: 1.5px solid #212529; padding: 4px 6px; height: 3.3in; max-height: 3.3in; display: flex; flex-direction: column; background: white; overflow: hidden; }
            
            /* Header with Logo */
            .header { display: flex; align-items: center; padding-bottom: 3px; margin-bottom: 3px; border-bottom: 1px solid #dee2e6; }
            .logo { width: 30px; height: 30px; object-fit: contain; flex-shrink: 0; }
            .header-text { flex: 1; text-align: center; min-width: 0; }
            .header-text h2 { margin: 0; font-size: 11px; font-weight: bold; color: #6c757d; letter-spacing: 1px; line-height: 1.1; }
            .header-text p { margin: 1px 0 0 0; font-size: 9px; color: #212529; font-weight: bold; line-height: 1.1; }
            
            /* Stock ID Box */
            .stock-id-box { border: 1.5px solid #212529; padding: 3px; text-align: center; margin-bottom: 4px; }
            .stock-id-label { font-size: 7px; text-transform: uppercase; letter-spacing: 1px; color: #6c757d; line-height: 1; }
            .stock-id-value { font-size: 14px; font-weight: bold; font-family: monospace; color: #212529; line-height: 1.1; }
            
            /* Data Table */
            .content-table { width: 100%; border-collapse: collapse; font-size: 9px; table-layout: fixed; }
            .content-table td { padding: 1px 4px; line-height: 1.15; }
            .content-table tr { border-bottom: 1px solid #dee2e6; }
            .content-table tr:last-child { border-bottom: none; }
            .field-label { font-weight: bold; width: 38%; color: #6c757d; text-transform: uppercase; font-size: 7px; }
            .field-value { text-align: right; color: #212529; font-size: 9px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0; }
            .weight-row { background: #fff3cd !important; }
            .weight-value { font-size: 11px; font-weight: bold; }
            
            /* QR Section at Bottom */
            .qr-section { margin-top: auto; padding-top: 3px; border-top: 1px solid #dee2e6; text-align: center; }
            .qr-hint { font-size: 6px; color: #6c757d; margin-bottom: 1px; line-height: 1; }
            .qr-container { display: inline-block; position: relative; }
            .qr-code { width: 60px; height: 60px; }
            .qr-overlay { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 1px 3px; font-size: 6px; font-weight: bold; color: #007bff; }
            
            /* Footer */
            .footer { text-align: center; font-size: 6px; color: #6c757d; margin-top: 2px; line-height: 1; }

            @media print {
              body { margin: 0; padding: 0; background: white; }
            }
          </style>
        </head><body>
          <div class="label"><div class="label-inner">
            <!-- Header with Logo -->
            <div class="header">
              <img src="/challenge-pack-logo.png" alt="Logo" class="logo" onerror="this.style.display='none';" />
              <div class="header-text">
                <h2>BIT REEL LABEL</h2>
                <p>Challenge Pack Industries</p>
              </div>
            </div>
            
            <!-- Stock ID Box -->
            <div class="stock-id-box">
              <div class="stock-id-label">Stock ID</div>
              <div class="stock-id-value">${reelToPrint.reel_id}</div>
            </div>
            
            <!-- Data Table -->
            <table class="content-table"><tbody>
              ${reelToPrint.parent_reel_id ? `<tr><td class="field-label">Parent Stock:</td><td class="field-value">${reelToPrint.parent_reel_id}</td></tr>` : ''}
              ${reelToPrint.reel_size ? `<tr><td class="field-label">Reel Size:</td><td class="field-value">${reelToPrint.reel_size}"</td></tr>` : ''}
              <tr><td class="field-label">RM Name:</td><td class="field-value">${reelToPrint.rm_name || reelToPrint.parent_rm_name || '-'}</td></tr>
              ${reelToPrint.mill_name ? `<tr><td class="field-label">Mill Name:</td><td class="field-value">${reelToPrint.mill_name}</td></tr>` : ''}
              ${reelToPrint.in_date ? `<tr><td class="field-label">In Date:</td><td class="field-value">${reelToPrint.in_date}</td></tr>` : ''}
              <tr class="weight-row"><td class="field-label">Weight:</td><td class="field-value weight-value">${reelToPrint.reel_weight || '-'} Kg</td></tr>
              ${reelToPrint.product ? `<tr><td class="field-label">Product:</td><td class="field-value">${reelToPrint.product}</td></tr>` : ''}
            </tbody></table>
            
            <!-- QR Section at Bottom -->
            <div class="qr-section">
              <div class="qr-hint">Scan QR Code to view label</div>
              <div class="qr-container">
                <img class="qr-code" src="${qrDataUrl}" alt="QR Code" />
                <div class="qr-overlay">CPI</div>
              </div>
            </div>
            
            <div class="footer">Label: ${response.data.label_number} | Printed: ${new Date().toLocaleDateString()}</div>
          </div></div>
          <script>
            var qrImg = document.querySelector('.qr-code');
            function triggerPrint() { setTimeout(function() { window.focus(); window.print(); }, 500); }
            if (qrImg && !qrImg.complete) {
              qrImg.onload = triggerPrint;
              qrImg.onerror = triggerPrint;
              setTimeout(triggerPrint, 3000);
            } else { triggerPrint(); }
          </script>
        </body></html>`;
        
        openPrintWindow(htmlContent);
        
        // Mark as printed
        handleMarkLabelPrinted(reelToPrint.reel_id);
      } else {
        toast.error(`Failed to upload: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Error uploading label:", error);
      toast.error("Failed to upload label to Google Drive");
    } finally {
      setUploadingLabel(false);
      setShowSizeSelector(false);
      setReelToPrint(null);
    }
  };

  // Get current label dimensions
  const getLabelDimensions = () => {
    if (useCustomSize && customLabelWidth && customLabelHeight) {
      return { width: parseFloat(customLabelWidth), height: parseFloat(customLabelHeight) };
    }
    // Use standard label dimensions (editable)
    return { width: parseFloat(standardLabelWidth) || 3, height: parseFloat(standardLabelHeight) || 4.5 };
  };

  const { width: labelWidth, height: labelHeight } = getLabelDimensions();

  // Generate QR code URL - points to Google Drive PDF when available
  const getQRData = (reel) => {
    if (!reel) return "";
    // Use the Google Drive link if available, otherwise use production URL
    if (reel.drive_link && reel.drive_link !== "None") return reel.drive_link;
    return `https://cpicore.in/label/${reel.reel_id}`;
  };

  return (
    <>
      {/* Label Button */}
      <Button
        onClick={handleOpenLabelDialog}
        variant="outline"
        className={cn(
          "relative border-orange-500/50 text-orange-400 hover:bg-orange-500/20",
          variant === "icon" && "p-2"
        )}
        data-testid="label-btn"
      >
        <Tag className="h-4 w-4 mr-2" />
        <span>Label</span>
        {pendingLabelsCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {pendingLabelsCount}
          </span>
        )}
        {/* Warning indicator for labels older than 1 day */}
        {oldestUnprintedDays >= 1 && pendingLabelsCount > 0 && (
          <AlertTriangle className="h-3 w-3 text-red-500 absolute -bottom-1 -right-1 animate-bounce" />
        )}
      </Button>

      {/* Label Size Selector Dialog */}
      <Dialog open={showSizeSelector} onOpenChange={setShowSizeSelector}>
        <DialogContent className="sm:max-w-md bg-gradient-to-br from-gray-900 to-gray-800 border-orange-500/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-orange-400 flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Select Label Size
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Size Options - Simplified */}
            <div className="space-y-3">
              {/* Standard Label Option with Edit */}
              <div 
                className={`p-3 rounded-lg transition-all ${
                  !useCustomSize 
                    ? 'bg-orange-500/20 border-2 border-orange-500' 
                    : 'bg-black/30 border-2 border-transparent hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="radio"
                      name="labelSize"
                      checked={!useCustomSize}
                      onChange={() => {
                        setUseCustomSize(false);
                        setEditingStandardSize(false);
                      }}
                      className="accent-orange-500"
                    />
                    <span className="text-white font-medium">
                      {standardLabelWidth}" x {standardLabelHeight}" (Standard Label)
                    </span>
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      setEditingStandardSize(!editingStandardSize);
                      setUseCustomSize(false);
                    }}
                    className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/20 h-8 px-2"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Editable Standard Size Inputs */}
                {editingStandardSize && !useCustomSize && (
                  <div className="mt-3 p-3 bg-black/30 rounded-lg border border-orange-500/30">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-orange-300 text-xs">Width (inches)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="1"
                          max="10"
                          value={standardLabelWidth}
                          onChange={(e) => setStandardLabelWidth(e.target.value)}
                          className="bg-black/50 border-orange-500/30 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-300 text-xs">Height (inches)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="1"
                          max="10"
                          value={standardLabelHeight}
                          onChange={(e) => setStandardLabelHeight(e.target.value)}
                          className="bg-black/50 border-orange-500/30 text-white mt-1"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-green-400 mt-2">
                      Standard size: {standardLabelWidth}" x {standardLabelHeight}"
                    </p>
                  </div>
                )}
              </div>
              
              {/* Custom Size Option */}
              <label 
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                  useCustomSize 
                    ? 'bg-orange-500/20 border-2 border-orange-500' 
                    : 'bg-black/30 border-2 border-transparent hover:bg-gray-800'
                }`}
              >
                <input
                  type="radio"
                  name="labelSize"
                  checked={useCustomSize}
                  onChange={() => {
                    setUseCustomSize(true);
                    setEditingStandardSize(false);
                  }}
                  className="accent-orange-500"
                />
                <span className="text-white font-medium">Manual Entry (Custom Size)</span>
              </label>
              
              {/* Custom Size Inputs */}
              {useCustomSize && (
                <div className="p-3 bg-black/30 rounded-lg border border-gray-700 ml-6">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-orange-300 text-xs">Width (inches)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="1"
                        max="10"
                        placeholder="e.g., 3"
                        value={customLabelWidth}
                        onChange={(e) => setCustomLabelWidth(e.target.value)}
                        className="bg-black/50 border-orange-500/30 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-orange-300 text-xs">Height (inches)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="1"
                        max="10"
                        placeholder="e.g., 5"
                        value={customLabelHeight}
                        onChange={(e) => setCustomLabelHeight(e.target.value)}
                        className="bg-black/50 border-orange-500/30 text-white mt-1"
                      />
                    </div>
                  </div>
                  {customLabelWidth && customLabelHeight && (
                    <p className="text-xs text-green-400 mt-2">
                      Custom size: {customLabelWidth}" x {customLabelHeight}"
                    </p>
                  )}
                </div>
              )}
            </div>

            {reelToPrint && (
              <div className="p-3 bg-black/30 rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400">Uploading & printing label for:</p>
                <p className="text-lg font-mono text-cyan-400 font-bold">{reelToPrint.reel_id}</p>
                <p className="text-sm text-gray-300">Weight: {reelToPrint.reel_weight || "-"} Kg</p>
              </div>
            )}

            {/* Upload Progress */}
            {uploadingLabel && (
              <div className="p-3 bg-black/50 rounded-lg border border-orange-500/30">
                <div className="flex items-center gap-2 text-orange-300">
                  <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                  <span>Uploading label to Google Drive...</span>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setShowSizeSelector(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                disabled={uploadingLabel}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmAndPrint}
                disabled={(useCustomSize && (!customLabelWidth || !customLabelHeight)) || uploadingLabel}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {uploadingLabel ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Printer className="h-4 w-4 mr-2" />
                    Upload & Print Label
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Label Dialog */}
      <Dialog open={showLabelDialog} onOpenChange={setShowLabelDialog}>
        <DialogContent className="w-[95vw] sm:max-w-5xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-gray-800 border-orange-500/50 text-white p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-2xl font-bold text-orange-400 flex flex-wrap items-center gap-2">
              <Tag className="h-5 w-5 sm:h-6 sm:w-6" />
              <span className="text-base sm:text-2xl">Bit Reels Ready for Label Printing</span>
              {pendingLabelsCount > 0 && (
                <span className="bg-red-600 text-white text-xs sm:text-sm rounded-full px-2 py-0.5">
                  {pendingLabelsCount} pending
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-3 sm:mt-4">
            {/* Warning for overdue labels */}
            {oldestUnprintedDays >= 1 && (
              <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-500/20 border-2 border-red-500 rounded-lg animate-pulse">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                  <span className="font-bold text-sm sm:text-lg">WARNING: Labels Overdue!</span>
                </div>
                <p className="text-red-300 mt-2 text-xs sm:text-sm">
                  Labels pending for <strong>{oldestUnprintedDays} day(s)</strong>. Print them now!
                </p>
              </div>
            )}
            
            {loadingBitReels ? (
              <div className="text-center py-8 text-gray-400">
                <Clock className="h-8 w-8 mx-auto mb-2 animate-spin" />
                <p>Loading bit reels...</p>
              </div>
            ) : bitReelsForLabel.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-gray-400 bg-black/30 rounded-lg">
                <Tag className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 opacity-50" />
                <p className="text-base sm:text-lg font-medium">No Bit Reels Pending</p>
                <p className="text-xs sm:text-sm mt-1 px-2">Complete Process 1 or 2 with partial usage to create bit reels.</p>
              </div>
            ) : (
              <>
                <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <p className="text-xs sm:text-sm text-orange-300">
                    <strong>{bitReelsForLabel.length}</strong> bit reel(s) ready for label printing
                  </p>
                </div>
                
                {/* Mobile Card Layout */}
                <div className="sm:hidden space-y-3">
                  {bitReelsForLabel.map((reel, idx) => (
                    <div key={reel.id || idx} className={`p-3 rounded-lg border ${reel.days_pending >= 1 ? 'bg-red-500/10 border-red-500/50' : 'bg-black/30 border-gray-700'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="font-mono text-cyan-400 font-bold text-sm">{reel.reel_id}</span>
                          {reel.days_pending >= 1 ? (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-red-500/30 text-red-400 font-bold">
                              {reel.days_pending}d!
                            </span>
                          ) : (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-yellow-500/30 text-yellow-400">
                              New
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handlePrintClick(reel)}
                          className={`${reel.days_pending >= 1 ? 'bg-red-500' : 'bg-orange-500'} text-white text-xs px-2 py-1`}
                        >
                          <Printer className="h-3 w-3 mr-1" />
                          Print
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <div><span className="text-gray-500">Parent:</span> <span className="text-white">{reel.parent_reel_id}</span></div>
                        <div><span className="text-gray-500">Weight:</span> <span className="text-green-400 font-bold">{reel.reel_weight || "-"} Kg</span></div>
                        <div><span className="text-gray-500">RM:</span> <span className="text-gray-300">{reel.rm_name || reel.parent_rm_name || "-"}</span></div>
                        <div><span className="text-gray-500">Size:</span> <span className="text-gray-300">{reel.reel_size || "-"}"</span></div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Desktop Table Layout */}
                <div className="hidden sm:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700 hover:bg-transparent">
                        <TableHead className="text-orange-400">Status</TableHead>
                        <TableHead className="text-orange-400">Bit Reel ID</TableHead>
                        <TableHead className="text-orange-400">Parent Stock ID</TableHead>
                        <TableHead className="text-orange-400">Parent Weight</TableHead>
                        <TableHead className="text-orange-400">Reel Size</TableHead>
                        <TableHead className="text-orange-400">Balance Weight</TableHead>
                        <TableHead className="text-orange-400">RM Name</TableHead>
                        <TableHead className="text-orange-400">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bitReelsForLabel.map((reel, idx) => (
                        <TableRow key={reel.id || idx} className={`border-gray-700 hover:bg-orange-500/10 ${reel.days_pending >= 1 ? 'bg-red-500/10' : ''}`}>
                          <TableCell>
                            {reel.days_pending >= 1 ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-red-500/30 text-red-400 border border-red-500/50 font-bold animate-pulse">
                                {reel.days_pending}d overdue
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/30 text-yellow-400 border border-yellow-500/50">
                                New
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-cyan-400 font-bold">{reel.reel_id}</TableCell>
                          <TableCell className="font-mono text-white">{reel.parent_reel_id}</TableCell>
                          <TableCell className="text-gray-300">{reel.parent_weight || reel.original_weight || "-"} Kg</TableCell>
                          <TableCell className="text-gray-300">{reel.reel_size || reel.parent_reel_size || "-"}"</TableCell>
                          <TableCell className="text-green-400 font-bold">{reel.reel_weight || "-"} Kg</TableCell>
                          <TableCell className="text-gray-300">{reel.rm_name || reel.parent_rm_name || "-"}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handlePrintClick(reel)}
                              className={`${reel.days_pending >= 1 ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'} text-white`}
                              data-testid={`print-label-${reel.reel_id}`}
                            >
                              <Printer className="h-4 w-4 mr-1" />
                              Print
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
            
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setShowLabelDialog(false)} className="border-gray-600 text-gray-300 hover:bg-gray-700 text-sm">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden Print Content for Label - Dynamic Size with Logo and QR Code */}
      {selectedLabelReel && (
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <div 
            ref={labelPrintRef} 
            style={{ 
              width: `${labelWidth}in`, 
              height: `${labelHeight}in`,
              padding: "8px", 
              fontFamily: "Arial, sans-serif", 
              fontSize: "10px", 
              background: "white", 
              color: "black",
              boxSizing: "border-box"
            }}
          >
            <div style={{ border: "2px solid black", padding: "8px", height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Header with Logo */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid black", paddingBottom: "6px", marginBottom: "6px" }}>
                <img 
                  src={LOGO_URL} 
                  alt="CPI Logo" 
                  style={{ width: "40px", height: "40px", objectFit: "contain" }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div style={{ textAlign: "center", flex: 1 }}>
                  <h2 style={{ margin: 0, fontSize: "12px", fontWeight: "bold" }}>BIT REEL LABEL</h2>
                  <p style={{ margin: "2px 0 0 0", fontSize: "9px" }}>Challenge Pack Industries</p>
                </div>
                {/* QR Code with CPI in center */}
                <div style={{ position: "relative", width: "60px", height: "60px" }}>
                  <QRCodeSVG
                    value={getQRData(selectedLabelReel)}
                    size={60}
                    level="H" // High error correction to allow logo overlay
                    includeMargin={false}
                  />
                  {/* CPI text overlay in center - small to not block scanning */}
                  <div style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "white",
                    padding: "0px 2px",
                    fontSize: "6px",
                    fontWeight: "bold",
                    color: "#000",
                    lineHeight: "1"
                  }}>
                    CPI
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", flex: 1 }}>
                <tbody>
                  <tr>
                    <td style={{ padding: "3px", fontWeight: "bold", width: "40%", borderBottom: "1px dotted #999" }}>Bit Reel ID:</td>
                    <td style={{ padding: "3px", fontSize: "12px", fontWeight: "bold", borderBottom: "1px dotted #999" }}>{selectedLabelReel.reel_id}</td>
                  </tr>
                  <tr style={{ background: "#f5f5f5" }}>
                    <td style={{ padding: "3px", fontWeight: "bold", borderBottom: "1px dotted #999" }}>Parent Stock:</td>
                    <td style={{ padding: "3px", borderBottom: "1px dotted #999" }}>{selectedLabelReel.parent_reel_id}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "3px", fontWeight: "bold", borderBottom: "1px dotted #999" }}>Parent Weight:</td>
                    <td style={{ padding: "3px", borderBottom: "1px dotted #999" }}>{selectedLabelReel.parent_weight || selectedLabelReel.original_weight || "-"} Kg</td>
                  </tr>
                  <tr style={{ background: "#f5f5f5" }}>
                    <td style={{ padding: "3px", fontWeight: "bold", borderBottom: "1px dotted #999" }}>Reel Size:</td>
                    <td style={{ padding: "3px", borderBottom: "1px dotted #999" }}>{selectedLabelReel.reel_size || selectedLabelReel.parent_reel_size || "-"}"</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "3px", fontWeight: "bold", borderBottom: "1px dotted #999" }}>Balance Wt:</td>
                    <td style={{ padding: "3px", fontSize: "12px", fontWeight: "bold", borderBottom: "1px dotted #999" }}>{selectedLabelReel.reel_weight || "-"} Kg</td>
                  </tr>
                  <tr style={{ background: "#f5f5f5" }}>
                    <td style={{ padding: "3px", fontWeight: "bold", borderBottom: "1px dotted #999" }}>RM Name:</td>
                    <td style={{ padding: "3px", borderBottom: "1px dotted #999" }}>{selectedLabelReel.rm_name || selectedLabelReel.parent_rm_name || "-"}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "3px", fontWeight: "bold" }}>Product:</td>
                    <td style={{ padding: "3px" }}>{selectedLabelReel.product || "-"}</td>
                  </tr>
                </tbody>
              </table>
              
              {/* Footer */}
              <div style={{ marginTop: "auto", paddingTop: "4px", borderTop: "1px dashed black", textAlign: "center", fontSize: "8px", color: "#666" }}>
                <p style={{ margin: 0 }}>Printed: {new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LabelPrinting;
